import { useState, useEffect, useCallback, useRef } from 'react';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { Product } from '@/data/products';
import { 
  fetchProductsPaginated, 
  getProductCount, 
  PaginatedProductsResult, 
  ProductCategory,
  fetchPageCursors
} from '@/services/productService';

interface UsePaginatedProductsOptions {
  category?: ProductCategory;
  pageSize?: number;
  enabled?: boolean;
}

interface UsePaginatedProductsResult {
  products: Product[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  fetchNextPage: () => Promise<void>;
  fetchPage: (page: number) => Promise<void>;
  reset: () => void;
}

/**
 * Hook for paginated product fetching with server-side pagination
 * Optimized for cost-effective Firestore queries
 */
export const usePaginatedProducts = ({ 
  category = 'all', 
  pageSize = 20,
  enabled = true 
}: UsePaginatedProductsOptions = {}): UsePaginatedProductsResult => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  // Use ref for pagination cursors to avoid dependency issues
  const pageCursorsRef = useRef<Map<number, QueryDocumentSnapshot<DocumentData> | null>>(new Map());
  
  const totalPages = Math.ceil(totalCount / pageSize);

  // Load total count
  const loadTotalCount = useCallback(async () => {
    try {
      const count = await getProductCount(category);
      setTotalCount(count);
    } catch (err) {
      setError('Failed to load product count');
    }
  }, [category]);

  // Load products for a specific page
  const loadPage = useCallback(async (page: number) => {
    if (!enabled) return;
    
    try {
      setLoading(true);
      setError(null);
      
      let cursor: QueryDocumentSnapshot<DocumentData> | null = null;
      
      if (page === 1) {
        cursor = null;
      } else {
        // Check if we have the cursor for the previous page
        cursor = pageCursorsRef.current.get(page - 1) || null;
        
        // If we don't have the cursor for a non-sequential jump, fetch intermediate cursors
        if (!cursor && page > 1) {
          
          try {
            const intermediateCursors = await fetchPageCursors(page, pageSize, category);
            
            // Merge the intermediate cursors into our existing cursors
            intermediateCursors.forEach((value, key) => {
              pageCursorsRef.current.set(key, value);
            });
            
            // Now try to get the cursor again
            cursor = pageCursorsRef.current.get(page - 1) || null;
          } catch (err) {
            // Fallback to sequential fetch
            
            // Fallback: sequentially fetch pages to build up cursors
            let currentCursor: QueryDocumentSnapshot<DocumentData> | null = null;
            
            for (let p = 1; p < page; p++) {
              if (!pageCursorsRef.current.has(p)) {
                const result = await fetchProductsPaginated(pageSize, category, currentCursor);
                if (result.lastDoc) {
                  pageCursorsRef.current.set(p, result.lastDoc);
                  currentCursor = result.lastDoc;
                }
              } else {
                currentCursor = pageCursorsRef.current.get(p) || null;
              }
            }
            
            cursor = currentCursor;
          }
        }
      }
      
      const result: PaginatedProductsResult = await fetchProductsPaginated(
        pageSize,
        category,
        cursor
      );
      
      setProducts(result.products);
      setHasMore(result.hasMore);
      setCurrentPage(page);
      
      // Store the cursor for the next page
      if (result.lastDoc) {
        pageCursorsRef.current.set(page, result.lastDoc);
      }
      
    } catch (err) {
      setError('Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, pageSize, category]);

  // Fetch next page (append to current products)
  const fetchNextPage = useCallback(async () => {
    if (!hasMore || loading) return;
    
    try {
      setLoading(true);
      
      const cursor = pageCursorsRef.current.get(currentPage);
      const result = await fetchProductsPaginated(pageSize, category, cursor);
      
      setProducts(prev => [...prev, ...result.products]);
      setHasMore(result.hasMore);
      setCurrentPage(prev => prev + 1);
      
      if (result.lastDoc) {
        pageCursorsRef.current.set(currentPage + 1, result.lastDoc);
      }
      
    } catch (err) {
      setError('Failed to load more products');
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, currentPage, pageSize, category]);

  // Fetch specific page
  const fetchPage = useCallback(async (page: number) => {
    if (page < 1 || page > totalPages) return;
    await loadPage(page);
  }, [loadPage, totalPages]);

  // Reset pagination
  const reset = useCallback(() => {
    setProducts([]);
    setCurrentPage(1);
    pageCursorsRef.current.clear();
    setHasMore(false);
    setError(null);
  }, []);

  // Initial load and when category changes
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    const initialize = async () => {
      // Reset state when category changes
      setProducts([]);
      setCurrentPage(1);
      pageCursorsRef.current.clear();
      setHasMore(false);
      setError(null);
      
      // Load total count
      try {
        const count = await getProductCount(category);
        setTotalCount(count);
      } catch (err) {
        console.error('Error fetching product count:', err);
        setError('Failed to load product count');
        setLoading(false);
        return;
      }
      
      // Load first page
      try {
        setLoading(true);
        setError(null);
        
        const result: PaginatedProductsResult = await fetchProductsPaginated(
          pageSize,
          category,
          null
        );
        
        setProducts(result.products);
        setHasMore(result.hasMore);
        setCurrentPage(1);
        
        // Store the cursor for the next page
        if (result.lastDoc) {
          pageCursorsRef.current.set(1, result.lastDoc);
        }
        
      } catch (err) {
        setError('Failed to load products');
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [category, enabled, pageSize]);

  return {
    products,
    loading,
    error,
    totalCount,
    currentPage,
    totalPages,
    hasMore,
    fetchNextPage,
    fetchPage,
    reset
  };
}; 